import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Guest } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import './venueCompareProposals.css'

const formatMoney = (value, currency) => {
    if (value === undefined || value === null || value === '') return '—'
    const amount = Number(value)
    if (Number.isNaN(amount)) return String(value)
    const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN')}`
}

// data.features is a dynamic list of boolean-feature labels (e.g. "Catering
// Included") — each proposal's own `features` map uses the exact same
// labels as keys, so the comparison rows are built from whatever the API
// returns rather than a hardcoded list.
const mapCompareItem = (item) => ({
    id: item.proposal_id,
    name: item.venue_name || 'Venue',
    totalPrice: formatMoney(item.total_price, item.currency),
    capacity: String(item.guest_capacity ?? '—'),
    venueAccess: item.venue_access || '—',
    features: item.features || {},
})

const CheckCircle = ({ value }) => (
    <span className={`venueCompareCheck ${value ? 'is-yes' : 'is-no'}`}>{value ? '✓' : '✕'}</span>
)

const VenueCompareProposals = () => {
    const navigate = useNavigate()
    const { state } = useLocation()
    const [vendors, setVendors] = useState([])
    const [featureLabels, setFeatureLabels] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)

    useEffect(() => {
        // /vendors/user_app/proposals/{id} (and .../compare) key off the RFP's
        // id, not the quotation's own id — confirmed 2026-08-17 (quotation.id
        // 404s / "No valid proposals found to compare", rfp_id works).
        const proposalIds = (state?.proposals || []).map((p) => p.rfpId).filter(Boolean)
        if (!proposalIds.length) {
            setVendors([])
            setLoading(false)
            return
        }

        setLoading(true)
        setLoadError(false)
        service
            .get(endpoints.venues.proposalsCompare, { proposal_ids: proposalIds.join(',') }, { silent: true })
            .then(({ data }) => {
                const body = data?.data ?? data
                const list = Array.isArray(body?.proposals) ? body.proposals : []
                setFeatureLabels(Array.isArray(body?.features) ? body.features : [])
                setVendors(list.map(mapCompareItem))
            })
            .catch(() => {
                setVendors([])
                setLoadError(true)
            })
            .finally(() => setLoading(false))
    }, [state])

    const rows = vendors

    const featureRows = featureLabels.map((label) => ({ key: label, label, type: 'bool' }))
    const allRows = [
        { key: 'totalPrice', label: 'Total Price', type: 'price' },
        { key: 'capacity', label: 'Guest Capacity', type: 'guests' },
        { key: 'venueAccess', label: 'Venue Access', type: 'text' },
        ...featureRows,
    ]

    if (!loading && (loadError || rows.length === 0)) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>
                        {loadError ? "We couldn't load this comparison. Please try again." : 'No proposals selected to compare.'}
                    </p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>Back to Proposals</button>
                </div>
            </section>
        )
    }

    return (
        <section className='commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='bsSectionTitle'>Compare Proposals</div>
                <p className='hotelCard__locationNote mb-3'>
                    {loading ? 'Loading comparison...' : `Comparing ${rows.length} proposals side by side`}
                </p>

                {loading ? (
                    <p className='hotelResultsEmpty'>Loading comparison...</p>
                ) : (
                    <div className='venueCompareTableWrap'>
                        <table className='venueCompareTable'>
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    {rows.map((vendor) => (
                                        <th key={vendor.id}>
                                            <div>{vendor.name}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {allRows.map((row) => (
                                    <tr key={row.key}>
                                        <td>{row.label}</td>
                                        {rows.map((vendor) => (
                                            <td key={vendor.id}>
                                                {row.type === 'bool' ? (
                                                    <CheckCircle value={!!vendor.features[row.key]} />
                                                ) : row.type === 'price' ? (
                                                    <div className='venueCompareTable__stacked'>
                                                        <span className='venueCompareTable__value'>{vendor.totalPrice}</span>
                                                        <span className='venueCompareTable__subLabel'>Total</span>
                                                    </div>
                                                ) : row.type === 'guests' ? (
                                                    <div className='venueCompareTable__stacked'>
                                                        <img src={Guest} alt="" className='venueCompareTable__icon' />
                                                        <span className='venueCompareTable__value'>{vendor.capacity}</span>
                                                    </div>
                                                ) : (
                                                    vendor[row.key]
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                <tr>
                                    <td />
                                    {rows.map((vendor) => (
                                        <td key={vendor.id}>
                                            <button
                                                type='button'
                                                className='commonBtn venueCompareTable__viewBtn'
                                                onClick={() => navigate(`/venue-proposal-detail/${vendor.id}`)}
                                            >
                                                View
                                            </button>
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                <p className='venueCompareTip'>
                    <strong>Tip:</strong>&nbsp;Consider both price and features when making your decision.
                </p>
            </div>
        </section>
    )
}

export default VenueCompareProposals
